import { useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext.js";
import type { SupportedLocale } from "@lakseborsen/shared";
import { SUPPORTED_LOCALES } from "@lakseborsen/shared";
import { useNotifications } from "../lib/useNotifications.js";
import TextSizeToggle from "./TextSizeToggle.js";
import styles from "./Nav.module.css";

const LOCALE_LABELS: Record<SupportedLocale, string> = {
  nb: "🇳🇴 NO",
  en: "🇬🇧 EN",
  de: "🇩🇪 DE",
  pl: "🇵🇱 PL",
  sv: "🇸🇪 SV",
  fi: "🇫🇮 FI",
  is: "🇮🇸 IS",
};

export default function Nav() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    isAuthenticated,
    isChecking,
    userName,
    isAdmin,
    isRiverAdmin,
    isValdOwner,
    getToken,
    login,
    logout,
    register,
  } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);

  const { notifications, unreadCount, markRead, markAllRead, reload } =
    useNotifications(getToken, isAuthenticated);

  function close() {
    setMenuOpen(false);
    setUserMenuOpen(false);
    setBellOpen(false);
  }

  async function handleNotificationClick(id: string, linkTo: string | null) {
    await markRead(id);
    setBellOpen(false);
    if (linkTo) navigate(linkTo);
  }

  return (
    <header className={styles.header}>
      <nav className={styles.nav}>
        <NavLink
          to="/"
          className={styles.logo}
          aria-label="Lakseborsen"
          onClick={close}
        >
          <span className={styles.logoText}>Lakseborsen</span>
        </NavLink>

        {/* Desktop links */}
        <ul className={styles.links}>
          <li>
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                isActive ? styles.linkActive : styles.link
              }
            >
              {t("nav.stats")}
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/historikk"
              className={({ isActive }) =>
                isActive ? styles.linkActive : styles.link
              }
            >
              {t("nav.history")}
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/elver"
              className={({ isActive }) =>
                isActive ? styles.linkActive : styles.link
              }
            >
              {t("nav.rivers")}
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/fiskekort"
              className={({ isActive }) =>
                isActive ? styles.linkActive : styles.link
              }
            >
              {t("nav.fiskeHer")}
            </NavLink>
          </li>
          <li><NavLink to="/selg-fiskekort" className={({ isActive }) => isActive ? styles.linkActive : styles.link}>{t("nav.sellFiskekort")}</NavLink></li>
          <li><NavLink to="/elveeierlag" className={({ isActive }) => isActive ? styles.linkActive : styles.link}>{t("nav.riverOwnersAssoc")}</NavLink></li>
          {isAuthenticated && (
            <li>
              <NavLink
                to="/fangst/ny"
                className={({ isActive }) =>
                  isActive ? styles.linkActive : styles.link
                }
              >
                {t("nav.registerCatch")}
              </NavLink>
            </li>
          )}
          {isAuthenticated && (
            <li>
              <NavLink
                to="/logg-fiskedag"
                className={({ isActive }) =>
                  isActive ? styles.linkActive : styles.link
                }
              >
                {t("nav.loggFiskedag")}
              </NavLink>
            </li>
          )}
        </ul>

        <div className={styles.actions}>
          <div className={styles.textSizeWrapper}>
            <TextSizeToggle compact />
          </div>
          <select
            className={styles.langPicker}
            value={i18n.language}
            onChange={(e) => i18n.changeLanguage(e.target.value)}
            aria-label={t("nav.language")}
          >
            {SUPPORTED_LOCALES.map((l) => (
              <option key={l} value={l}>
                {LOCALE_LABELS[l]}
              </option>
            ))}
          </select>

          {isAuthenticated && (
            <div className={styles.bellWrapper}>
              <button
                className={styles.bellBtn}
                aria-label={t("notifications.title")}
                aria-expanded={bellOpen}
                onClick={() => {
                  setUserMenuOpen(false);
                  const opening = !bellOpen;
                  setBellOpen(opening);
                  if (opening) reload();
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {unreadCount > 0 && (
                  <span className={styles.bellBadge}>
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
              {bellOpen && (
                <>
                  <div
                    className={styles.userMenuBackdrop}
                    onClick={() => setBellOpen(false)}
                  />
                  <div
                    className={`${styles.userMenuDropdown} ${styles.bellDropdown}`}
                  >
                    <div className={styles.bellHeader}>
                      <span className={styles.bellTitle}>
                        {t("notifications.title")}
                      </span>
                      {unreadCount > 0 && (
                        <button
                          className={styles.bellMarkAll}
                          onClick={() => markAllRead()}
                        >
                          {t("notifications.markAllRead")}
                        </button>
                      )}
                    </div>
                    {notifications.length === 0 ? (
                      <p className={styles.bellEmpty}>
                        {t("notifications.empty")}
                      </p>
                    ) : (
                      <ul className={styles.bellList}>
                        {notifications.map((n) => (
                          <li key={n.id}>
                            <button
                              className={`${styles.bellItem} ${
                                n.readAt
                                  ? styles.bellItemRead
                                  : styles.bellItemUnread
                              }`}
                              onClick={() =>
                                handleNotificationClick(n.id, n.linkTo)
                              }
                            >
                              <span className={styles.bellItemTitle}>
                                {n.title}
                              </span>
                              {n.bodyText && (
                                <span className={styles.bellItemBody}>
                                  {n.bodyText}
                                </span>
                              )}
                              <time className={styles.bellItemTime}>
                                {new Date(n.createdAt).toLocaleString("nb", {
                                  dateStyle: "short",
                                  timeStyle: "short",
                                })}
                              </time>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {isAuthenticated ? (
            <div className={styles.userMenuWrapper}>
              <button
                className={styles.userMenuBtn}
                onClick={() => setUserMenuOpen((o) => !o)}
                aria-expanded={userMenuOpen}
                aria-haspopup="true"
              >
                {userName ?? t("nav.profile")}
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="currentColor"
                  aria-hidden="true"
                  style={{ marginLeft: 4 }}
                >
                  <path
                    d="M2 4l4 4 4-4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {userMenuOpen && (
                <>
                  <div
                    className={styles.userMenuBackdrop}
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <div className={styles.userMenuDropdown}>
                    <Link
                      to="/fangst/ny"
                      className={`${styles.userMenuItem} ${styles.userMenuItemPrimary}`}
                      onClick={close}
                    >
                      {t("nav.registerCatch")}
                    </Link>
                    <Link
                      to="/logg-fiskedag"
                      className={`${styles.userMenuItem} ${styles.userMenuItemPrimary}`}
                      onClick={close}
                    >
                      {t("nav.loggFiskedag")}
                    </Link>
                    <div className={styles.userMenuDivider} />
                    <Link
                      to="/fangst"
                      className={styles.userMenuItem}
                      onClick={close}
                    >
                      {t("nav.myCatches")}
                    </Link>
                    <Link
                      to="/mine/fiskekort"
                      className={styles.userMenuItem}
                      onClick={close}
                    >
                      {t("nav.myFiskekort")}
                    </Link>
                    <Link
                      to="/mine/bookinger"
                      className={styles.userMenuItem}
                      onClick={close}
                    >
                      {t("nav.myBookings")}
                    </Link>
                    <Link
                      to="/profil"
                      className={styles.userMenuItem}
                      onClick={close}
                    >
                      {t("nav.profile")}
                    </Link>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        className={styles.userMenuItem}
                        onClick={close}
                      >
                        {t("nav.admin")}
                      </Link>
                    )}
                    {isAdmin && (
                      <Link
                        to="/admin/fotokonkurranse"
                        className={styles.userMenuItem}
                        onClick={close}
                      >
                        {t("nav.adminPhotoContest")}
                      </Link>
                    )}
                    {(isRiverAdmin || isAdmin) && (
                      <Link
                        to="/river-admin"
                        className={styles.userMenuItem}
                        onClick={close}
                      >
                        {t("nav.riverAdmin")}
                      </Link>
                    )}
                    {isValdOwner && (
                      <Link
                        to="/vald-admin"
                        className={styles.userMenuItem}
                        onClick={close}
                      >
                        {t("nav.valdAdmin")}
                      </Link>
                    )}
                    <div className={styles.userMenuDivider} />
                    <button
                      className={styles.userMenuLogout}
                      onClick={() => {
                        logout();
                        close();
                      }}
                    >
                      {t("nav.logout")}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : isChecking ? (
            <button className={styles.authBtn} disabled aria-busy="true">
              &nbsp;
            </button>
          ) : (
            <div className={styles.authGroup}>
              <button className={styles.authBtn} onClick={register}>
                {t("nav.registerLink")}
              </button>
              <button
                className={`${styles.authBtn} ${styles.authBtnPrimary}`}
                onClick={login}
              >
                {t("nav.login")}
              </button>
            </div>
          )}

          {/* Hamburger — mobile only */}
          <button
            className={styles.menuBtn}
            aria-label={menuOpen ? t("nav.closeMenu") : t("nav.openMenu")}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
          >
            {menuOpen ? (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            ) : (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <line x1="3" y1="7" x2="21" y2="7" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="17" x2="21" y2="17" />
              </svg>
            )}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <>
          <div className={styles.mobileBackdrop} onClick={close} />
          <nav className={styles.mobileMenu} aria-label={t("nav.menu")}>
            <ul className={styles.mobileLinks}>
              {/* Fishing — the core fisher actions, first and most prominent */}
              {isAuthenticated && (
                <>
                  <li>
                    <NavLink
                      to="/fangst/ny"
                      className={({ isActive }) =>
                        isActive
                          ? styles.mobileLinkActive
                          : `${styles.mobileLink} ${styles.mobileLinkPrimary}`
                      }
                      onClick={close}
                    >
                      {t("nav.registerCatch")}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/logg-fiskedag"
                      className={({ isActive }) =>
                        isActive
                          ? styles.mobileLinkActive
                          : `${styles.mobileLink} ${styles.mobileLinkPrimary}`
                      }
                      onClick={close}
                    >
                      {t("nav.loggFiskedag")}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/fangst"
                      end
                      className={({ isActive }) =>
                        isActive ? styles.mobileLinkActive : styles.mobileLink
                      }
                      onClick={close}
                    >
                      {t("nav.myCatches")}
                    </NavLink>
                  </li>
                </>
              )}

              {/* Explore — always visible, relevant to every fisher */}
              <li className={styles.mobileSectionDivider}>
                <span className={styles.mobileSectionLabel}>
                  {t("nav.sectionExplore")}
                </span>
              </li>
              <li>
                <NavLink
                  to="/"
                  end
                  className={({ isActive }) =>
                    isActive ? styles.mobileLinkActive : styles.mobileLink
                  }
                  onClick={close}
                >
                  {t("nav.stats")}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/historikk"
                  className={({ isActive }) =>
                    isActive ? styles.mobileLinkActive : styles.mobileLink
                  }
                  onClick={close}
                >
                  {t("nav.history")}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/elver"
                  className={({ isActive }) =>
                    isActive ? styles.mobileLinkActive : styles.mobileLink
                  }
                  onClick={close}
                >
                  {t("nav.rivers")}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/fiskekort"
                  className={({ isActive }) =>
                    isActive ? styles.mobileLinkActive : styles.mobileLink
                  }
                  onClick={close}
                >
                  {t("nav.fiskeHer")}
                </NavLink>
              </li>
              {/* My account — personal management, secondary to registering */}
              {isAuthenticated && (
                <>
                  <li className={styles.mobileSectionDivider}>
                    <span className={styles.mobileSectionLabel}>
                      {t("nav.sectionAccount")}
                    </span>
                  </li>
                  <li>
                    <NavLink
                      to="/mine/fiskekort"
                      className={({ isActive }) =>
                        isActive ? styles.mobileLinkActive : styles.mobileLink
                      }
                      onClick={close}
                    >
                      {t("nav.myFiskekort")}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/mine/bookinger"
                      className={({ isActive }) =>
                        isActive ? styles.mobileLinkActive : styles.mobileLink
                      }
                      onClick={close}
                    >
                      {t("nav.myBookings")}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/profil"
                      className={({ isActive }) =>
                        isActive ? styles.mobileLinkActive : styles.mobileLink
                      }
                      onClick={close}
                    >
                      {t("nav.profile")}
                    </NavLink>
                  </li>
                </>
              )}

              {/* Management — only shown to admins/owners who actually have these roles */}
              {(isAdmin || isRiverAdmin || isValdOwner) && (
                <>
                  <li className={styles.mobileSectionDivider}>
                    <span className={styles.mobileSectionLabel}>
                      {t("nav.sectionManagement")}
                    </span>
                  </li>
                  {isAdmin && (
                    <li>
                      <NavLink
                        to="/admin"
                        className={({ isActive }) =>
                          isActive ? styles.mobileLinkActive : styles.mobileLink
                        }
                        onClick={close}
                      >
                        {t("nav.admin")}
                      </NavLink>
                    </li>
                  )}
                  {isAdmin && (
                    <li>
                      <NavLink
                        to="/admin/fotokonkurranse"
                        className={({ isActive }) =>
                          isActive ? styles.mobileLinkActive : styles.mobileLink
                        }
                        onClick={close}
                      >
                        {t("nav.adminPhotoContest")}
                      </NavLink>
                    </li>
                  )}
                  {(isRiverAdmin || isAdmin) && (
                    <li>
                      <NavLink
                        to="/river-admin"
                        className={({ isActive }) =>
                          isActive ? styles.mobileLinkActive : styles.mobileLink
                        }
                        onClick={close}
                      >
                        {t("nav.riverAdmin")}
                      </NavLink>
                    </li>
                  )}
                  {isValdOwner && (
                    <li>
                      <NavLink
                        to="/vald-admin"
                        className={({ isActive }) =>
                          isActive ? styles.mobileLinkActive : styles.mobileLink
                        }
                        onClick={close}
                      >
                        {t("nav.valdAdmin")}
                      </NavLink>
                    </li>
                  )}
                </>
              )}

              {/* For owners — promotional, not relevant to fishers registering a catch */}
              <li className={styles.mobileSectionDivider}>
                <span className={styles.mobileSectionLabel}>
                  {t("nav.sectionForOwners")}
                </span>
              </li>
              <li>
                <NavLink
                  to="/selg-fiskekort"
                  className={({ isActive }) =>
                    isActive ? styles.mobileLinkActive : styles.mobileLink
                  }
                  onClick={close}
                >
                  {t("nav.sellFiskekort")}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/elveeierlag"
                  className={({ isActive }) =>
                    isActive ? styles.mobileLinkActive : styles.mobileLink
                  }
                  onClick={close}
                >
                  {t("nav.riverOwnersAssoc")}
                </NavLink>
              </li>

              {isAuthenticated ? (
                <li className={styles.mobileLogout}>
                  <button
                    className={styles.mobileLogoutBtn}
                    onClick={() => {
                      logout();
                      close();
                    }}
                  >
                    {t("nav.logout")}
                  </button>
                </li>
              ) : (
                !isChecking && (
                  <>
                    <li className={styles.mobileLogout}>
                      <button
                        className={`${styles.mobileLogoutBtn} ${styles.mobileLogoutBtnPrimary}`}
                        onClick={() => {
                          login();
                          close();
                        }}
                      >
                        {t("nav.login")}
                      </button>
                    </li>
                    <li className={styles.mobileLogout}>
                      <button
                        className={styles.mobileLogoutBtn}
                        onClick={() => {
                          register();
                          close();
                        }}
                      >
                        {t("nav.registerLink")}
                      </button>
                    </li>
                  </>
                )
              )}
            </ul>

            {/* Text size */}
            <div className={styles.mobileLangSection}>
              <span className={styles.mobileLangLabel}>
                {t("textSize.label")}
              </span>
              <TextSizeToggle />
            </div>

            {/* Language picker */}
            <div className={styles.mobileLangSection}>
              <span className={styles.mobileLangLabel}>
                {t("nav.language")}
              </span>
              <select
                className={styles.mobileLangSelect}
                value={i18n.language}
                onChange={(e) => {
                  i18n.changeLanguage(e.target.value);
                  close();
                }}
                aria-label={t("nav.language")}
              >
                {SUPPORTED_LOCALES.map((l) => (
                  <option key={l} value={l}>
                    {LOCALE_LABELS[l]}
                  </option>
                ))}
              </select>
            </div>
          </nav>
        </>
      )}
    </header>
  );
}
